import { Box, Typography } from "@material-ui/core";
import { CodeDialog } from "components/CodeDialog";
import { AppList } from "components/list/AppList";
import { ALL_ROWS_PER_PAGE_OPTION } from "components/list/pagination";
import { useSPPScannerContext } from "components/Scanner/GlobalSPPScanner";
import { ScrollingWrapper } from "components/ScrollingWrapper";
import { envSettings } from "core/EnvSettings";
import React, { useCallback, useMemo, useState } from "react";
import { useListContext, useRecordContext, useTranslate } from "react-admin";

import { isScannerOperationMode } from "../PickingList/components/OperationModeToggleFilter";
import { PalletsActions } from "./components/PalletsActions";
import { PalletsFilters } from "./components/PalletsFilter";
import { ConfirmYesNoDialog } from "./components/ConfirmYesNoDialog";
import { StoreByScanner } from "./components/ScanSlots";
import { useCanScanToCreatePallet } from "./hooks/useCanScanToCreatePallet";
import { useOperationMode } from "./hooks/useOperationMode";
import { useOrderServiceType } from "./hooks/useOrderServiceType";
import { usePalletsSelection } from "./hooks/usePalletsSelection";
import { usePalletsSummary } from "./hooks/usePalletsSummary";
import { useScannerOperations } from "./hooks/useScannerOperations";
import { PalletsGrid } from "./PalletsGrid";
import { palletsUseStyles } from "./Styles";

export const sortDefaultValue = { field: "sscc", order: "DESC" };
const PALLETS_PER_PAGE = 100;
const PALLETS_ROWS_PER_PAGE_OPTIONS = [PALLETS_PER_PAGE, 200, ALL_ROWS_PER_PAGE_OPTION];

const PalletsListContent = ({
	order,
	terminalId,
	translate,
	isCSOrIncomingPlukk,
	operationMode,
	isLoadingMode,
	isMoveToPreparationMode,
	selectedPalletsForLoading,
	setSelectedPalletsForLoading,
	setBarcodeValue,
	setBarcodeDialogOpen,
	setBarcodeTitle,
	allocationResult,
	allocationResultOpen,
	setAllocationResultOpen,
	setAllocationResult,
	summaryTotals,
	scanModifiedPalletIds,
	storagePlaces,
	setStoragePlaces,
	allowStorePallet,
	record,
	palletsBaseFilter,
	isScanToCreateActive,
	setIsLoadingMode,
	setIsMoveToPreparationMode,
	setScanModifiedPalletIds,
	registerScanHandler,
	registerTargetPage,
	isConnected,
	isManualAuthorizationRef,
}) => {
	const { refetch } = useListContext();

	const {
		scannedPallet,
		scannedStorage,
		stacking,
		joinStack,
		palletValidationState,
		storageValidationState,
		suggestedStoragePlace,
		lastScannerMessage,
		isScannerLoading,
		clearScannerState,
	} = useScannerOperations({
		record,
		operationMode,
		palletsBaseFilter,
		allowStorePallet,
		isLoadingMode,
		isMoveToPreparationMode,
		isScanToCreateActive,
		recordPrepPlaceId: record.storage_place_id || record.area_id,
		setSelectedPalletsForLoading,
		registerScanHandler,
		registerTargetPage,
		isConnected,
		isManualAuthorizationRef,
		setScanModifiedPalletIds,
		setStoragePlaces,
		refetch,
	});

	const { isCrossDock } = useOrderServiceType(record);
	const isSelectionMode = isLoadingMode || isMoveToPreparationMode;
	// Cross-dock uses the arrival scanner panel outside selection mode (mirroring
	// useScannerOperations) and the scanner panel for scan-to-create.
	const isScannerPanelVisible =
		(isCSOrIncomingPlukk || (isCrossDock && (!isSelectionMode || isScanToCreateActive))) &&
		(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE ||
			isScannerOperationMode(operationMode));

	return (
		<>
			{/* Scanner Mode UI - between filters and grid */}
			{isScannerPanelVisible && (
				<Box display="flex" justifyContent="center" alignItems="center" mb={3} mt={2}>
					<StoreByScanner
						translate={translate}
						palletValidationState={palletValidationState}
						storageValidationState={storageValidationState}
						suggestedStoragePlace={suggestedStoragePlace}
						allowClear={false}
						lastMessage={lastScannerMessage}
						scannedPallet={scannedPallet}
						scannedStorage={scannedStorage}
						stackedPallets={stacking.pallets}
						isLoading={isScannerLoading}
					/>
				</Box>
			)}

			{stacking.pending && (
				<ConfirmYesNoDialog
					titleKey="tab.pallets.stack_confirmation.title"
					messageKey="tab.pallets.stack_confirmation.message"
					translate={translate}
					onConfirm={stacking.confirm}
					onReject={stacking.reject}
				>
					{[scannedPallet?.code, ...stacking.pallets.map((p) => p.code), stacking.pending.code]
						.filter(Boolean)
						.map((sscc) => (
							<Typography key={sscc} variant="body2">
								<strong>{sscc}</strong>
							</Typography>
						))}
				</ConfirmYesNoDialog>
			)}

			{/* Scanned place already holds a stack the pallet may join */}
			{joinStack.pending && (
				<ConfirmYesNoDialog
					titleKey="tab.pallets.join_stack_confirmation.title"
					messageKey="tab.pallets.join_stack_confirmation.message"
					translate={translate}
					onConfirm={joinStack.confirm}
					onReject={joinStack.reject}
				>
					<Typography variant="body2">
						<strong>{joinStack.pending.pallet?.sscc}</strong>
					</Typography>
				</ConfirmYesNoDialog>
			)}

			<ScrollingWrapper>
				<PalletsGrid
					order={order}
					terminalId={terminalId}
					operationMode={operationMode}
					isLoadingMode={isLoadingMode}
					isMoveToPreparationMode={isMoveToPreparationMode}
					selectedPalletsForLoading={selectedPalletsForLoading}
					setSelectedPalletsForLoading={setSelectedPalletsForLoading}
					setBarcodeValue={setBarcodeValue}
					setBarcodeDialogOpen={setBarcodeDialogOpen}
					setBarcodeTitle={setBarcodeTitle}
					allocationResult={allocationResult}
					allocationResultOpen={allocationResultOpen}
					setAllocationResultOpen={setAllocationResultOpen}
					setAllocationResult={setAllocationResult}
					summaryTotals={summaryTotals}
					scanModifiedPalletIds={scanModifiedPalletIds}
					storagePlaces={storagePlaces}
					setStoragePlaces={setStoragePlaces}
					allowStorePallet={allowStorePallet}
				/>
			</ScrollingWrapper>
		</>
	);
};

export const PalletsList = ({ order, terminalId }) => {
	const classes = palletsUseStyles();
	const record = useRecordContext();
	const translate = useTranslate();

	const { registerScanHandler, registerTargetPage, isConnected, isManualAuthorizationRef } =
		useSPPScannerContext();

	const [allocationResultOpen, setAllocationResultOpen] = useState(false);
	const [allocationResult, setAllocationResult] = useState(null);
	const [allocationLoading, setAllocationLoading] = useState(false);
	const [statusChangeLoading, setStatusChangeLoading] = useState(false);
	const [storagePlaces, setStoragePlaces] = useState({});
	const [scanModifiedPalletIds, setScanModifiedPalletIds] = useState([]);

	// Barcode dialog state
	const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
	const [barcodeValue, setBarcodeValue] = useState("");
	const [barcodeTitle, setBarcodeTitle] = useState("");

	const { isOutgoingPlukk, isCSOrIncomingPlukk, isCrossDock } = useOrderServiceType(record);

	const palletsBaseFilter = useMemo(() => ({ order_id: record.id }), [record.id]);

	const canScanToCreatePallet = useCanScanToCreatePallet(record);

	const { operationMode, setOperationMode, defaultOperationMode, isScanToCreatePalletActive } =
		useOperationMode({ canScanToCreatePallet });

	const {
		isLoadingMode,
		setIsLoadingMode,
		isMoveToPreparationMode,
		setIsMoveToPreparationMode,
		selectedPalletsForLoading,
		setSelectedPalletsForLoading,
		isSelectionMode,
	} = usePalletsSelection({ record });

	const { summaryTotals, fetchSummaryTotals } = usePalletsSummary({
		orderId: record?.id,
	});

	const allowStorePallet = useCallback((pallet) => {
		let allowed = pallet?.arrived_datetime === null;
		if (envSettings.REACT_APP_FF_STORAGE_MODULE) {
			allowed = allowed && !pallet?.is_storage_place_confirmed && !pallet?.loaded_into_truck;
		}
		return allowed;
	}, []);

	const isScannerFlowActive = envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE
		? (isOutgoingPlukk && isSelectionMode) || isCSOrIncomingPlukk || isCrossDock
		: isScannerOperationMode(operationMode);

	const filterDefaultValues = {
		order_id: order.id,
	};

	return (
		<>
			<AppList
				basePath={`/order/${order.id}/pallets`}
				resource="pallets"
				sort={sortDefaultValue}
				perPage={PALLETS_PER_PAGE}
				rowsPerPageOptions={PALLETS_ROWS_PER_PAGE_OPTIONS}
				bulkActionButtons={false}
				exporter={false}
				className={classes.listRoot}
				title={`${translate("tab.pallets.pallets_for_order")} #${order.id}`}
				filters={
					<PalletsFilters
						operationMode={operationMode}
						setOperationMode={setOperationMode}
						isSelectionMode={isSelectionMode}
						isOutgoingPlukk={isOutgoingPlukk}
						isScannerMode={isScannerFlowActive}
						canScanToCreatePallet={canScanToCreatePallet}
					/>
				}
				actions={
					<PalletsActions
						order={order}
						operationMode={operationMode}
						isLoadingMode={isLoadingMode}
						isMoveToPreparationMode={isMoveToPreparationMode}
						setIsLoadingMode={setIsLoadingMode}
						setIsMoveToPreparationMode={setIsMoveToPreparationMode}
						selectedPalletsForLoading={selectedPalletsForLoading}
						setSelectedPalletsForLoading={setSelectedPalletsForLoading}
						allocationLoading={allocationLoading}
						setAllocationLoading={setAllocationLoading}
						setAllocationResult={setAllocationResult}
						setAllocationResultOpen={setAllocationResultOpen}
						statusChangeLoading={statusChangeLoading}
						setStatusChangeLoading={setStatusChangeLoading}
						setOperationMode={setOperationMode}
						defaultOperationMode={defaultOperationMode}
						allowStorePallet={allowStorePallet}
						fetchSummaryTotals={fetchSummaryTotals}
						isScanToCreateEnabled={canScanToCreatePallet}
						isScanToCreateActive={isScanToCreatePalletActive}
					/>
				}
				filterDefaultValues={filterDefaultValues}
			>
				<PalletsListContent
					order={order}
					terminalId={terminalId}
					translate={translate}
					isCSOrIncomingPlukk={isCSOrIncomingPlukk}
					operationMode={operationMode}
					isLoadingMode={isLoadingMode}
					isMoveToPreparationMode={isMoveToPreparationMode}
					selectedPalletsForLoading={selectedPalletsForLoading}
					setSelectedPalletsForLoading={setSelectedPalletsForLoading}
					setBarcodeValue={setBarcodeValue}
					setBarcodeDialogOpen={setBarcodeDialogOpen}
					setBarcodeTitle={setBarcodeTitle}
					allocationResult={allocationResult}
					allocationResultOpen={allocationResultOpen}
					setAllocationResultOpen={setAllocationResultOpen}
					setAllocationResult={setAllocationResult}
					summaryTotals={summaryTotals}
					scanModifiedPalletIds={scanModifiedPalletIds}
					storagePlaces={storagePlaces}
					setStoragePlaces={setStoragePlaces}
					allowStorePallet={allowStorePallet}
					record={record}
					palletsBaseFilter={palletsBaseFilter}
					isScanToCreateActive={isScanToCreatePalletActive}
					setIsLoadingMode={setIsLoadingMode}
					setIsMoveToPreparationMode={setIsMoveToPreparationMode}
					setScanModifiedPalletIds={setScanModifiedPalletIds}
					registerScanHandler={registerScanHandler}
					registerTargetPage={registerTargetPage}
					isConnected={isConnected}
					isManualAuthorizationRef={isManualAuthorizationRef}
				/>
			</AppList>

			{/* Barcode Dialog */}
			<CodeDialog
				open={barcodeDialogOpen}
				type="barcode"
				onClose={() => setBarcodeDialogOpen(false)}
				value={barcodeValue}
				title={barcodeTitle || "SSCC Barcode"}
			/>
		</>
	);
};
